/**
 * Filterstatus van de Professional Academy: leeft in de URL (?vak=…&reg=…) zodat een
 * zoekopdracht deelbaar en herlaadbaar is. De reistijd-instelling (km) staat óók in
 * de URL, maar de eigen postcode nooit — die blijft in localStorage.
 */
import type { ProHoofdmap } from '../../services/professionalAcademyApi';

export type PeRegister = 'NVAB' | 'RSC_RNVC' | 'NOBCO_EIA' | 'NIP';
export type ProLeervormFilter = 'e_learning' | 'klassikaal';
export type ProSortering = 'relevantie' | 'start' | 'prijs' | 'pe';

export interface ProFilters {
  vak: string | null;
  map: ProHoofdmap | null;
  q: string | null;
  reg: PeRegister[];
  pe: number;
  vorm: ProLeervormFilter | null;
  examen: boolean;
  dag: number[];
  hobby: boolean;
  /** "Alle cursussen van A tot Z" */
  alles: boolean;
  /** Max. afstand in km; null = Heel Nederland. Alleen actief bij klassikaal. */
  km: number | null;
  sort: ProSortering;
}

export const LEGE_FILTERS: ProFilters = {
  vak: null, map: null, q: null, reg: [], pe: 0, vorm: null, examen: false, dag: [], hobby: false, alles: false, km: null, sort: 'relevantie',
};

const REGISTERS: PeRegister[] = ['NVAB', 'RSC_RNVC', 'NOBCO_EIA', 'NIP'];
const HOOFDMAPPEN: ProHoofdmap[] = ['welzijn', 'trauma', 'psa', 'arbo', 'ld', 'passie'];
const SORTERINGEN: ProSortering[] = ['relevantie', 'start', 'prijs', 'pe'];

export function filtersUitParams(sp: URLSearchParams): ProFilters {
  const map = sp.get('map');
  const vorm = sp.get('vorm');
  const sort = sp.get('sort');
  const km = parseInt(sp.get('km') ?? '', 10);
  return {
    vak: sp.get('vak') || null,
    map: HOOFDMAPPEN.includes(map as ProHoofdmap) ? (map as ProHoofdmap) : null,
    q: sp.get('q')?.trim() || null,
    reg: (sp.get('reg') ?? '').split(',').map((r) => r.trim().toUpperCase()).filter((r): r is PeRegister => REGISTERS.includes(r as PeRegister)),
    pe: Math.max(0, parseInt(sp.get('pe') ?? '0', 10) || 0),
    vorm: vorm === 'e_learning' || vorm === 'klassikaal' ? vorm : null,
    examen: sp.get('examen') === '1',
    dag: [...new Set((sp.get('dag') ?? '').split(',').map((d) => parseInt(d, 10)).filter((d) => d >= 1 && d <= 6))].sort(),
    hobby: sp.get('hobby') === '1',
    alles: sp.get('alles') === '1',
    km: Number.isFinite(km) && km > 0 ? km : null,
    sort: SORTERINGEN.includes(sort as ProSortering) ? (sort as ProSortering) : 'relevantie',
  };
}

export function paramsUitFilters(f: ProFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.vak) sp.set('vak', f.vak);
  if (f.map) sp.set('map', f.map);
  if (f.q) sp.set('q', f.q);
  if (f.reg.length) sp.set('reg', f.reg.join(','));
  if (f.pe > 0) sp.set('pe', String(f.pe));
  if (f.vorm) sp.set('vorm', f.vorm);
  if (f.examen) sp.set('examen', '1');
  if (f.dag.length) sp.set('dag', f.dag.join(','));
  if (f.hobby) sp.set('hobby', '1');
  if (f.alles) sp.set('alles', '1');
  if (f.km) sp.set('km', String(f.km));
  if (f.sort !== 'relevantie') sp.set('sort', f.sort);
  return sp;
}

/** Actieve filters → grid-weergave. Hobby-schakelaar, sortering en km tellen niet mee. */
export function heeftFilters(f: ProFilters): boolean {
  return Boolean(f.alles || f.vak || f.map || f.q || f.reg.length || f.pe > 0 || f.vorm || f.examen || f.dag.length);
}

/** De reistijd-slider werkt alleen bij klassikaal / workshop. */
export function reistijdActief(f: ProFilters): boolean {
  return f.vorm === 'klassikaal' && f.km !== null;
}
